import {MutableRefObject, useEffect, useState} from "react";
import {
  PolicyFieldNames,
  settlementFrequencyDataLoading
} from "@/pages/policy/ProductPolicyApply/data";
import {WdiPanel} from "@/components/WdiControls/WdiPanel";
import {FieldProps, WdiCardTitle} from "@/components/WdiControls";
import {Enum} from "@/enums/system";
import {Col, Form, Row} from "antd";
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from "@/components/WdiControls/WdiSelect";
import RebateStandardApp from "@/pages/policy/ProductPolicyApply/components/rebateStandard";
import {OpenModeEnum} from "@/pages/policy/ProductPolicyApply/components/apply";
import moment from "moment";
import {Product} from "@/services/mdm/product";
import {ResponseCode} from "@/utils/response";
import {WdiTreeParseSelect, WdiTreeSelectComponentStates} from "@/components/WdiControls/WdiTreeParseSelect";
import {EC} from "@/services/mdm/ec";

type BasicReplenishDetailProps = {
  formRef: MutableRefObject<any>,
  openMode?: OpenModeEnum
  isLoaded?: boolean
};

const productLevels = [
  Enum.ProductLevel.PRODUCT1,
  Enum.ProductLevel.PRODUCT2,
  Enum.ProductLevel.PRODUCT3,
  Enum.ProductLevel.PRODUCT4,
  Enum.ProductLevel.PRODUCT5,
]

type CacheDataLoadingProps = {
  version: number,
  dataLoading: Promise<API.ResponseResult<any[]>>,
  disableLevels: number[],
  level: number,
  props: any
}

//达量、返利产品
//树状结构，只列出激活&ProductType=1&层次是第1-5层&用户相关品牌下的，单选；支付方式为货补池(经销商)、搭赠池(经销商)时才显示且必填，否则清空隐藏；
const defaultProductLoading = {
  version: 0,
  disableLevels: [],
  level: -1,
  props: {
    textField: 'codeOrName',
    valueField: 'productId',
    id: 'productId',
    pId: 'parentId',
    level: 'productLevel',
  },
}


const ProductPolicyDetailApplyPanel: React.FC<BasicReplenishDetailProps> = (props: BasicReplenishDetailProps) => {
  const isDisabled = props.openMode === OpenModeEnum.UPDATE_PARTLY;
  const [cacheDataLoadingState, setCacheDataLoadingState] = useState<{
    version: number,
    data: Map<PolicyFieldNames, CacheDataLoadingProps>
  }>({version: 0, data: new Map()});
  const [policyPeriodIsEq, setPolicyPeriodIsEq] = useState<boolean>(true);
  const paymentMethod = Form.useWatch('paymentMethod', props.formRef.current);
  const settlementFrequency = Form.useWatch('settlementFrequency', props.formRef.current);
  const policyDate = Form.useWatch('policyDate', props.formRef.current);
  const ecIds = Form.useWatch('ecIds', props.formRef.current);

  const getTreeData = (fieldName: PolicyFieldNames) => {
    const cacheDataLoading = cacheDataLoadingState.data.get(fieldName);
    return (
      cacheDataLoading?.dataLoading ??
      Promise.resolve({
        code: ResponseCode.SUCCESS,
        data: []
      })).then(
      (response) => {
        const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
          Object.assign(cacheDataLoading?.props ?? {}, {
            disabledLevel: cacheDataLoading?.disableLevels ?? []
          }),
          response.data,
        );
        return {treeData, dict};
      },
    );
  };

  useEffect(() => {

    const reachAndRebateDataLoding = {
      dataLoading: Product.searchNoRoot({
        productTypes: '1',
        withPermissions: true,
        isActive: true
      }),
      ...defaultProductLoading
    }
    cacheDataLoadingState.data.set('productIdsReach', reachAndRebateDataLoding)
    cacheDataLoadingState.data.set('productIdsRebate', reachAndRebateDataLoding)
    cacheDataLoadingState.data.set('productIdsReachEx', reachAndRebateDataLoding)
    cacheDataLoadingState.data.set('productIdsRebateEx', reachAndRebateDataLoding)
    //指定货补产品
    //树状结构，只列出激活&ProductType=1 &层次是第1-5层，单选；支付方式为货补池(经销商)、搭赠池(经销商)时才显示且必填，否则清空隐藏；
    //变更 允许ProductType in (1,2)
    cacheDataLoadingState.data.set('productIdsFg', {
      dataLoading: Product.searchNoRoot({
        productTypes: '1',
        productLevels: '1,2,3,4,5',
        isActive: true
      }),
      ...defaultProductLoading
    })
    setCacheDataLoadingState({version: cacheDataLoadingState.version + 1, data: cacheDataLoadingState.data})
  }, [])

  useEffect(() => {
    if (!props.isLoaded) {
      return;
    }
    const values = props.formRef?.current?.getFieldsValue([
      'productIdsReach', 'productIdsRebate', 'productIdsReachEx', 'productIdsRebateEx'
    ])
    if (values.productIdsReach && values.productIdsReach?.length) {
      Product.get(values.productIdsReach[0].value).then(res => {
        const productLevel = res?.data?.productLevel ?? -1
        updateDisableLevelState("productIdsReach", productLevel);
      })
    }
    if (values.productIdsRebate && values.productIdsRebate?.length) {
      Product.get(values.productIdsRebate[0].value).then(res => {
        const productLevel = res?.data?.productLevel ?? -1
        updateDisableLevelState("productIdsRebate", productLevel);
      })
    }
    if (values.productIdsReachEx && values.productIdsReachEx?.length) {
      Product.get(values.productIdsReachEx[0].value).then(res => {
        const productLevel = res?.data?.productLevel ?? -1
        updateDisableLevelState("productIdsReachEx", productLevel);
      })
    }
    if (values.productIdsRebateEx && values.productIdsRebateEx?.length) {
      Product.get(values.productIdsRebateEx[0].value).then(res => {
        const productLevel = res?.data?.productLevel ?? -1
        updateDisableLevelState("productIdsRebateEx", productLevel);
      })
    }
  }, [props.isLoaded])

  useEffect(() => {
    if (!policyDate || policyDate.length != 2 || !policyDate[0] || !policyDate[1]) {
      return;
    }
    const startPeriod = moment(policyDate[0]).format('yyyyMM')
    const endPeriod = moment(policyDate[1]).format('yyyyMM')
    setPolicyPeriodIsEq(startPeriod == endPeriod)
  }, [policyDate])

  //支付方式非货补池(经销商)、搭赠池(经销商)时，清空指定货补产品别
  useEffect(() => {
    if (![Enum.PaymentMethod.FG_POOL, Enum.PaymentMethod.BUY_GIVE_POOL].includes(paymentMethod)) {
      props.formRef.current?.setFieldsValue({productIdsFg: undefined});
    }
  }, [paymentMethod])

  const selectsRemoteSearch = async (selectValues: Set<any>, optionMap: Map<any, WdiTreeSelectComponentStates>) => {
    let firstProductLevel = optionMap.values().next().value?.productLevel ?? -1;
    const productRes = await Product.searchNoRoot({
      productTypes: [Enum.ProductType.FINISHED].join() ,
      withPermissions: true,
      isActive: true,
      productIds: Array.from(selectValues).join(),
    });
    const productMap = new Map((productRes?.data??[]).map(item => ([item.productId, item])));
    return Array.from(selectValues).map((selectValue: any) => {
      const product = productMap.get(selectValue)
      if (!productLevels.includes(firstProductLevel)) {
        firstProductLevel = product?.productLevel ?? -1
      }
      return {
        value: product?.productId ?? selectValue,
        label: product?.codeOrName ?? '主键:' + selectValue,
        level: product?.productLevel ?? -1,
        isRed: product?.productLevel != firstProductLevel
      }
    })
  }

  const inputsRemoteSearch = async (inputKeys: Set<any>, optionMap: Map<any, WdiTreeSelectComponentStates>) => {
    let firstProductLevel = optionMap.values().next().value?.level ?? -1;
    const result: any[] = [];
    for (let inputKey of inputKeys) {
      const productRes = await Product.searchNoRoot({
        productTypes: [Enum.ProductType.FINISHED].join() ,
        withPermissions: true,
        isActive: true,
        productCodeOrNameEq: inputKey
      })
      const product = productRes?.data?.[0];
      if (!productLevels.includes(firstProductLevel)) {
        firstProductLevel = product?.productLevel ?? -1
      }
      result.push({
        value: product?.productId,
        label: product?.codeOrName ?? inputKey,
        level: product?.productLevel ?? -1,
        isRed: product?.productLevel != firstProductLevel
      });
    }
    return result;
  }

  const productTreeOnChange = (fieldName: PolicyFieldNames,value: any) => {
    if (!value || value.length == 0) {
      updateDisableLevelState(fieldName, -1);
    }
  }

  const updateDisableLevelState = (
    fieldName: PolicyFieldNames,
    level: number,
  ) => {
    const cacheDataLoading = cacheDataLoadingState.data.get(fieldName);
    if (!cacheDataLoading) return;
    cacheDataLoadingState.data.set(fieldName, {
      version: cacheDataLoading.version + 1,
      disableLevels: level == -1 ? [] : productLevels.filter(item => item != level),
      level: level,
      props: cacheDataLoading.props,
      dataLoading: cacheDataLoading.dataLoading
    });
    setCacheDataLoadingState({version: cacheDataLoadingState.version + 1, data: cacheDataLoadingState.data})
  }

  return (
    <WdiPanel collapsed={true} title={<WdiCardTitle iconType={Enum.IconType.BASE_INFO} title={'政策条款'}/>}>
      <Row gutter={[16, 0]}>
        <Col className="gutter-row" span={4} key="productIdsReach">
          <Form.Item
            name="productIdsReach"
            label="达量产品"
            rules={[{required: (!isDisabled), message: '达量产品不能为空!'}]}
          >
            <WdiTreeParseSelect
              disabled={isDisabled}
              reloadKey={cacheDataLoadingState.data.get('productIdsReach')?.version}
              placeholder={'请选择达量产品'}
              multiple={true}
              labelInValue={true}
              onChange={(value: any) => productTreeOnChange('productIdsReach', value)}
              onExSelect={(value: any, node: any, dict: any) => {
                const values = props.formRef.current?.getFieldValue('productIdsReach');
                const rtv =  FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
                props.formRef.current?.setFieldsValue({
                  productIdsReach: rtv,
                  productIdsRebate: rtv
                });
                updateDisableLevelState("productIdsReach", node.level);
                updateDisableLevelState("productIdsRebate", node.level);
              }}
              dataLoading={() => getTreeData('productIdsReach')}
              selectsRemoteSearch={selectsRemoteSearch}
              inputsRemoteSearch={inputsRemoteSearch}
            />
          </Form.Item>
        </Col>

        <Col className="gutter-row" span={4} key="productIdsRebate">
          <Form.Item
            name="productIdsRebate"
            label="返利产品"
            rules={[{required: (!isDisabled), message: '返利产品不能为空!'}]}
          >
            <WdiTreeParseSelect
              disabled={isDisabled}
              reloadKey={cacheDataLoadingState.data.get('productIdsRebate')?.version}
              placeholder={'请选择返利产品'}
              multiple={true}
              labelInValue={true}
              onChange={(value: any) => productTreeOnChange('productIdsRebate', value)}
              onExSelect={(value: any, node: any, dict: any) => {
                updateDisableLevelState("productIdsRebate", node.level);
                const values = props.formRef.current?.getFieldValue("productIdsRebate");
                const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
                props.formRef.current?.setFieldsValue({
                  productIdsRebate: rtv
                });
              }}
              dataLoading={() => getTreeData("productIdsRebate")}
              selectsRemoteSearch={selectsRemoteSearch}
              inputsRemoteSearch={inputsRemoteSearch}
            />
          </Form.Item>
        </Col>


        <Col className="gutter-row" span={4} key="productIdsReachEx">
          <Form.Item
            name="productIdsReachEx"
            label="排除达量产品"
          >
            <WdiTreeParseSelect
              disabled={isDisabled}
              reloadKey={cacheDataLoadingState.data.get('productIdsReachEx')?.version}
              placeholder={'请选择排除达量产品'}
              multiple={true}
              labelInValue={true}
              onChange={(value: any) => productTreeOnChange('productIdsReachEx', value)}
              onExSelect={(value: any, node: any, dict: any) => {
                const values = props.formRef.current?.getFieldValue("productIdsReachEx");
                const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
                props.formRef.current?.setFieldsValue({
                  productIdsReachEx: rtv,
                  productIdsRebateEx: rtv
                });
                updateDisableLevelState("productIdsReachEx", node.level);
                updateDisableLevelState("productIdsRebateEx", node.level);
              }}
              dataLoading={() => getTreeData("productIdsReachEx")}
              selectsRemoteSearch={selectsRemoteSearch}
              inputsRemoteSearch={inputsRemoteSearch}
            />
          </Form.Item>
        </Col>

        <Col className="gutter-row" span={4} key="productIdsRebateEx">
          <Form.Item
            name="productIdsRebateEx"
            label="排除返利产品"
          >
            <WdiTreeParseSelect
              disabled={isDisabled}
              reloadKey={cacheDataLoadingState.data.get('productIdsRebateEx')?.version}
              placeholder={'请选择排除返利产品'}
              multiple={true}
              labelInValue={true}
              onChange={(value: any) => productTreeOnChange('productIdsRebateEx', value)}
              onExSelect={(value: any, node: any, dict: any) => {
                updateDisableLevelState("productIdsRebateEx", node.level);
                const values = props.formRef.current?.getFieldValue("productIdsRebateEx");
                const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
                props.formRef.current?.setFieldsValue({
                  productIdsRebateEx: rtv
                });
              }}
              dataLoading={() => getTreeData("productIdsRebateEx")}
              selectsRemoteSearch={selectsRemoteSearch}
              inputsRemoteSearch={inputsRemoteSearch}
            />
          </Form.Item>
        </Col>

        <Col className="gutter-row" span={4} key="settlementFrequency">
          <Form.Item
            name="settlementFrequency"
            label="结案频率"
            rules={[{required: true, message: '结案频率不能为空!'}]}
          >
            <WdiSelect
              disabled={isDisabled}
              {...{
                placeholder: '请选择结案频率',
                dataLoading: settlementFrequencyDataLoading
              }}
            />
          </Form.Item>
        </Col>

        {/*转账/账扣/票扣/费用池(经销商)/货补池(经销商)/第三方付款*/}
        <Col className="gutter-row" span={4} key="paymentMethod">
          <Form.Item
            name="paymentMethod"
            label="支付方式"
            rules={[{required: true, message: '支付方式不能为空!'}]}
          >
            <WdiSelect
              disabled={isDisabled}
              {...{
                reloadKey: ecIds,
                placeholder: '请选择支付方式',
                dataLoading: () => EC.getPaymentMethodsByIds(WdiSelectFormat.getvalue(ecIds)).then(res=>{
                  return (res?.data??[]).filter(item=>item.value!=Enum.PaymentMethod.FREE_GOODS).map(item=>({
                    value: item.value,
                    label: item.name,
                  }))
                })
              }}
            />
          </Form.Item>
        </Col>

        {
          [Enum.PaymentMethod.FG_POOL, Enum.PaymentMethod.BUY_GIVE_POOL].includes(paymentMethod) &&
          <Col className="gutter-row" span={4} key="productIdsFg">
            <Form.Item
              name="productIdsFg"
              label="指定货补产品别"
              rules={[{required: true, message: '指定货补产品别不能为空!'}]}
            >
              <WdiTreeSelect
                disabled={isDisabled}
                reloadKey={cacheDataLoadingState.data.get("productIdsFg")?.version}
                {...{
                  placeholder: '请选择指定货补产品别',
                  dataLoading: () => getTreeData('productIdsFg')
                }}
              />
            </Form.Item>
          </Col>
        }

        <Col className="gutter-row" span={24}>
          <Form.Item
            name="rebateStandard"
            label={'返利标准'}
            rules={[{required: true, message: '返利标准不能为空!'}]}
          >
            <RebateStandardApp openMode={props.openMode}
                               initialFormulaRebateIsRequired={settlementFrequency && settlementFrequency > 1 && !policyPeriodIsEq}/>
          </Form.Item>
        </Col>
      </Row>
    </WdiPanel>
  );
}

export default ProductPolicyDetailApplyPanel;
